import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableHead, TableBody, TableRow,
    TableCell, Chip, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, CircularProgress, MenuItem, Alert,
    IconButton, Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { directorAPI, downloadDocument } from '../../services/api';
import useAuth from '../../hooks/useAuth';

const statusColors = { pending: 'warning', accepted: 'success', refused: 'error' };
const statusLabels = { pending: 'En attente', accepted: 'Acceptée', refused: 'Refusée' };

export default function DirectorCandidatures() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [candidatures, setCandidatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [reviewDialog, setReviewDialog] = useState(null);
    const [reviewForm, setReviewForm] = useState({ status: 'accepted', director_comment: '' });
    const [drhDialog, setDrhDialog] = useState(null);
    const [drhForm, setDrhForm] = useState({ contract_start_date: '', contract_end_date: '' });
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const { data } = await directorAPI.getCandidatures();
            setCandidatures(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleReview = async () => {
        try {
            const { data } = await directorAPI.reviewCandidature(reviewDialog.id, reviewForm);
            toast.success(reviewForm.status === 'accepted' ? 'Candidature acceptée' : 'Candidature refusée');
            if (data.warnings?.length) {
                data.warnings.forEach(w => toast.warning(w, { autoClose: 8000 }));
            }
            setReviewDialog(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors du traitement');
        }
    };

    const handleDRHValidation = async () => {
        try {
            if (!drhForm.contract_start_date || !drhForm.contract_end_date) {
                return toast.error('Veuillez remplir toutes les dates');
            }
            const { data } = await directorAPI.validateDRH(drhDialog.id, drhForm);
            toast.success(data.message || 'Validation DRH réussie');
            if (data.warnings?.length) {
                data.warnings.forEach(w => toast.warning(w, { autoClose: 8000 }));
            }
            setDrhDialog(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de la validation DRH');
        }
    };

    const handleDownload = async (docId, name) => {
        try {
            const { data } = await downloadDocument(docId);
            const url = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click(); a.remove();
        } catch (err) {
            toast.error('Erreur de téléchargement');
        }
    };

    const filtered = filter
        ? candidatures.filter(c => c.status === filter)
        : candidatures;

    const colSpan = isAdmin ? 8 : 7;

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight={700}>Gérer les demandes</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip label="Toutes" onClick={() => setFilter('')} color={!filter ? 'primary' : 'default'} clickable />
                    <Chip label="En attente" onClick={() => setFilter('pending')} color={filter === 'pending' ? 'warning' : 'default'} clickable />
                    <Chip label="Acceptées" onClick={() => setFilter('accepted')} color={filter === 'accepted' ? 'success' : 'default'} clickable />
                    <Chip label="Refusées" onClick={() => setFilter('refused')} color={filter === 'refused' ? 'error' : 'default'} clickable />
                </Box>
            </Box>

            {filtered.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                    <Typography color="text.secondary">Aucune candidature trouvée</Typography>
                </Paper>
            ) : (
                <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <div className="responsive-table-wrapper">
                        <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                                <TableCell />
                                <TableCell sx={{ fontWeight: 600 }}>Candidat</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Offre</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Filière</TableCell>
                                {isAdmin && <TableCell sx={{ fontWeight: 600 }}>ISTA</TableCell>}
                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map(c => (
                                <React.Fragment key={c.id}>
                                    <TableRow hover>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                                                {expandedId === c.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>{c.user?.prenom} {c.user?.nom}</Typography>
                                            <Typography variant="caption" color="text.secondary">{c.user?.email}</Typography>
                                        </TableCell>
                                        <TableCell>{c.job_offer?.title || '—'}</TableCell>
                                        <TableCell>{c.job_offer?.filieres?.map(f => f.nom).join(', ') || '—'}</TableCell>
                                        {isAdmin && (
                                            <TableCell>
                                                {c.job_offer?.ista ? (
                                                    <Chip label={c.job_offer.ista.code} size="small" color="info" variant="outlined" />
                                                ) : '—'}
                                            </TableCell>
                                        )}
                                        <TableCell>{new Date(c.created_at).toLocaleDateString('fr-FR')}</TableCell>
                                        <TableCell>
                                            <Chip label={statusLabels[c.status]} color={statusColors[c.status]} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            {c.status === 'pending' && (
                                                <>
                                                    <IconButton size="small" color="success"
                                                        onClick={() => { setReviewDialog(c); setReviewForm({ status: 'accepted', director_comment: '' }); }}>
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                    <IconButton size="small" color="error"
                                                        onClick={() => { setReviewDialog(c); setReviewForm({ status: 'refused', director_comment: '' }); }}>
                                                        <CancelIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                            {c.status === 'accepted' && !c.validated_drh_at && (
                                                <Button 
                                                    size="small" variant="outlined" color="primary" 
                                                    onClick={() => {
                                                        setDrhDialog(c);
                                                        setDrhForm({ contract_start_date: '', contract_end_date: '' });
                                                    }}
                                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                                >
                                                    Valider DRH
                                                </Button>
                                            )}
                                            {c.validated_drh_at && (
                                                <Chip label="DRH Validée" color="info" size="small" variant="outlined" />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={colSpan} sx={{ p: 0 }}>
                                            <Collapse in={expandedId === c.id}>
                                                <Box sx={{ p: 3, bgcolor: '#fafbfc' }}>
                                                    <Typography variant="subtitle2" gutterBottom>Détails du candidat</Typography>
                                                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
                                                        <Typography variant="body2"><strong>CIN:</strong> {c.cin || '—'}</Typography>
                                                        <Typography variant="body2"><strong>Profession:</strong> {c.profession || '—'}</Typography>
                                                        <Typography variant="body2"><strong>Statut:</strong> {
                                                            { retraite: 'Retraité(e)', actif_prive: 'Actif — Privé', actif_public: 'Actif — Public', actif_public_men: 'Actif — Public (MEN)' }[c.statut_professionnel] || '—'
                                                        }</Typography>
                                                    </Box>
                                                    {c.diplomes && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="subtitle2">Diplômes</Typography>
                                                            <Typography variant="body2" color="text.secondary">{c.diplomes}</Typography>
                                                        </Box>
                                                    )}
                                                    {c.documents?.length > 0 && (
                                                        <Box>
                                                            <Typography variant="subtitle2" gutterBottom>Documents ({c.documents.length})</Typography>
                                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                                {c.documents.map(doc => (
                                                                    <Chip
                                                                        key={doc.id}
                                                                        icon={<DownloadIcon />}
                                                                        label={doc.original_name}
                                                                        onClick={() => handleDownload(doc.id, doc.original_name)}
                                                                        clickable variant="outlined" size="small"
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                    {c.director_comment && (
                                                        <Alert severity={c.status === 'accepted' ? 'success' : 'error'} sx={{ mt: 2 }}>
                                                            <strong>Commentaire:</strong> {c.director_comment}
                                                        </Alert>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                        </Table>
                    </div>
                </Paper>
            )}

            {/* Review Dialog */}
            <Dialog open={!!reviewDialog} onClose={() => setReviewDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {reviewForm.status === 'accepted' ? 'Accepter la candidature' : 'Refuser la candidature'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Candidat: <strong>{reviewDialog?.user?.prenom} {reviewDialog?.user?.nom}</strong> — Offre: <strong>{reviewDialog?.job_offer?.title}</strong>
                    </Typography>
                    <TextField
                        fullWidth multiline rows={3}
                        label="Commentaire (optionnel)"
                        value={reviewForm.director_comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, director_comment: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReviewDialog(null)} sx={{ textTransform: 'none' }}>Annuler</Button>
                    <Button
                        variant="contained"
                        color={reviewForm.status === 'accepted' ? 'success' : 'error'}
                        onClick={handleReview}
                        sx={{ textTransform: 'none' }}
                    >
                        {reviewForm.status === 'accepted' ? 'Accepter' : 'Refuser'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DRH Validation Dialog */}
            <Dialog open={!!drhDialog} onClose={() => setDrhDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Validation DRH</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3 }} color="text.secondary">
                        Saisissez les dates de validité d'engagement pour générer le contrat de <strong>{drhDialog?.user?.prenom} {drhDialog?.user?.nom}</strong>.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Date de début"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={drhForm.contract_start_date}
                            onChange={(e) => setDrhForm({ ...drhForm, contract_start_date: e.target.value })}
                        />
                        <TextField
                            label="Date de fin"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={drhForm.contract_end_date}
                            onChange={(e) => setDrhForm({ ...drhForm, contract_end_date: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setDrhDialog(null)} color="inherit">Annuler</Button>
                    <Button onClick={handleDRHValidation} variant="contained" color="primary" disabled={!drhForm.contract_start_date || !drhForm.contract_end_date}>
                        Valider & Générer Contrat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
